import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import * as Sentry from '@sentry/react';
import ProfileManager from './services/api/ProfileManager';
import ErrorView from './views/error';
import PusherProvider from './providers/PusherProvider';
import PrintProvider from './providers/PrintProvider';
import RNWebviewResolver from './services/resolvers/rnwebview/RNWebviewResolver';
import HelpersProvider from './providers/HelpersProvider';
import AuthProvider from './providers/AuthProvider';
import BrandProvider, { useBrand } from './providers/BrandProvider';
import SideEffectsProvider from './providers/SideEffectsProvider';
import Router from './routes/Router';
import EnvironmentBanner from './components/banners/EnvironmentBanner';
import OngoingOrdersProvider from './providers/OngoingOrdersProvider';
import FooterProvider from './providers/FooterProvider';
import { ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import { antdLocaleFor } from './services/localization/antdLocale';
import StatsProvider from './providers/StatsProvider';
import MetaInfo from './components/meta/MetaInfo';
import Theme from './styles/Theme';
import ColorHelpers from './services/helpers/ColorHelpers';
import { DateTime, Settings } from 'luxon';

function BrandedApp() {
  const { company } = useSelector((state) => state.company);
  const brand = useBrand();
  // Subscribing re-renders on languageChanged, so antd's strings switch at runtime.
  const { i18n } = useTranslation();

  useEffect(() => {
    function handleEvent(message) {
      new RNWebviewResolver(message?.data).resolve();
    }

    document.addEventListener('message', handleEvent);

    return () => document.removeEventListener('message', handleEvent);
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      Sentry.getCurrentScope()
        .setUser({
          id: company?.id,
          type: 'staff',
        })
        .setTag('company_id', company?.id);
    }
  }, [company?.id]);

  useEffect(() => {
    if (company?.timezone) {
      Settings.defaultZone = company?.timezone;
    }
  }, [company?.timezone]);

  const antTheme = useMemo(() => {
    const colorPrimary = brand?.primaryColor ?? Theme.color.primary;
    // antd derives its "light tint" tokens (selected menu item, active option, …)
    // by generating a palette from colorPrimary, which inverts for near-black
    // seeds — the neutral white-label #161616 rendered selected/active states as
    // solid dark blocks. Mixing with white is deterministic for any brand color.
    const colorPrimaryBg = ColorHelpers.tint(colorPrimary, 0.92);
    const colorPrimaryBgHover = ColorHelpers.tint(colorPrimary, 0.85);

    return {
      token: {
        colorPrimary,
        ...(colorPrimaryBg && { colorPrimaryBg, colorPrimaryBgHover }),
        fontFamily: 'Inter',
        colorBorder: '#DEDEDE',
      },
      components: {
        Modal: {
          zIndexPopupBase: 1000,
        },
        Dropdown: {
          zIndexPopupBase: 1500,
        },
        DatePicker: {
          zIndexPopupBase: 5000,
        },
        Button: {
          controlHeightLG: 44, // default is ~40
          fontSizeLG: 18,
          paddingInlineLG: 28,
        },
      },
    };
  }, [brand?.primaryColor]);

  return (
    <ConfigProvider theme={antTheme} componentSize="large" locale={antdLocaleFor(i18n.language).antd}>
      <EnvironmentBanner />
      <AuthProvider>
        <HelpersProvider>
          <StatsProvider>
            <PusherProvider>
              <SideEffectsProvider>
                <PrintProvider>
                  <OngoingOrdersProvider>
                    <FooterProvider>
                      <Router />
                    </FooterProvider>
                  </OngoingOrdersProvider>
                </PrintProvider>
              </SideEffectsProvider>
            </PusherProvider>
          </StatsProvider>
        </HelpersProvider>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          newestOnTop
          pauseOnHover={false}
          className="!px-sm z-max"
          toastClassName="my-2 !rounded-lg"
        />
        <MetaInfo />
      </AuthProvider>
    </ConfigProvider>
  );
}

export default function App() {
  const handleError = () => {
    ProfileManager.pauseOrders(DateTime.local().endOf('day'));
  };

  return (
    <Sentry.ErrorBoundary fallback={<ErrorView />} onError={handleError}>
      <BrandProvider>
        <BrandedApp />
      </BrandProvider>
    </Sentry.ErrorBoundary>
  );
}
